'use client';

import { HTMLAttributes, forwardRef, ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import styles from './Popover.module.css';

type PopoverPosition = 'top' | 'right' | 'bottom' | 'left';

interface PopoverProps extends HTMLAttributes<HTMLDivElement> {
  trigger: ReactNode;
  position?: PopoverPosition;
  children: ReactNode;
  showArrow?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const Popover = forwardRef<HTMLDivElement, PopoverProps>(({
  trigger,
  position = 'bottom',
  children,
  showArrow = false,
  open: controlledOpen,
  onOpenChange,
  className = '',
  ...props
}, ref) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const wrapperRef = useRef<HTMLDivElement>(null);

  const setOpen = useCallback((val: boolean) => {
    if (isControlled) {
      onOpenChange?.(val);
    } else {
      setInternalOpen(val);
    }
  }, [isControlled, onOpenChange]);

  const handleToggle = () => setOpen(!isOpen);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, setOpen]);

  return (
    <div ref={wrapperRef} className={`${styles.wrapper} ${className}`} {...props}>
      <div
        className={styles.trigger}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        {trigger}
      </div>
      {isOpen && (
        <div
          ref={ref}
          className={`${styles.popover} ${styles[position]}`}
          role="dialog"
        >
          {showArrow && <div className={`${styles.arrow} ${styles[`arrow${position.charAt(0).toUpperCase() + position.slice(1)}`]}`} />}
          <div className={styles.content}>{children}</div>
        </div>
      )}
    </div>
  );
});

Popover.displayName = 'Popover';
export default Popover;
